// react components
import React, {
    useEffect,
    useState,
} from 'react'
import {
    IonContent,
    IonPage,
} from '@ionic/react'
import axios, {
    CancelTokenSource,
} from 'axios'
import {
    useDispatch,
    useSelector,
} from 'react-redux'
import {
    useHistory,
    useLocation,
} from 'react-router-dom'

// components
import {
    LinkHelperSite,
    LoaderSite,
    LoaderPageSite,
    SeoBlockSite,
} from 'components'

// data
import {
    defaultReduxState,
    reduxFormRefreshLearnPageSite,
    reduxModalErrorEventHandlerSite,
} from 'data'

// pages
import {
    ContentBlockSite,
    LearnSideMenuTabSite,
    NotFoundPortfolioSite,
} from 'pages'

// serializers
import {
    PageDetailSiteSerializer,
    PortfolioPageContentListSiteSerializer,
    errorStatusTypeSite,
} from 'serializers/site'

// services
import {
    axiosErrorHandlerSite,
    checkPorfolioUserGroups,
    getApiUrlSiteV2,
    getAxiosHeadersSite,
    getDataSite,
    getStylesToRetrieve,
    parseQuerySite,
} from 'services'

// props
type LearnPageSiteProps = {
    detailId?: string
    devId: string
    learnPageSlug: string
    pageSlug: string
}

// main
export const LearnPageSite: React.FC<LearnPageSiteProps> = React.memo(({
    detailId,
    devId,
    learnPageSlug,
    pageSlug,
}) => {

    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation()
    const reduxAuth = useSelector((state: defaultReduxState) => state.reduxAuth)
    const reduxCacheSite = useSelector((state: defaultReduxState) => state.reduxCacheSite)
    const reduxModalSite = useSelector((state: defaultReduxState) => state.reduxModalSite)
    const reduxFormSiteRefreshLearnPage = useSelector((state: defaultReduxState) => state.reduxFormSite.refreshLearnPage)

    const deviceType = reduxModalSite.deviceType

    const [axiosCancelToken, setAxiosCancelToken] = useState<CancelTokenSource | undefined>(undefined)
    const [currencyId, setCurrencyId] = useState<number | undefined>(reduxModalSite.currencyId)
    const [errorStatus, setErrorStatus] = useState<errorStatusTypeSite>({})
    const [isLoading, setIsLoading] = useState(false)
    const [isLoadingNext, setIsLoadingNext] = useState(false)
    const [languageId, setLanguageId] = useState<number>(reduxModalSite.languageId)
    const [learnMenu, setLearnMenu] = useState<PageDetailSiteSerializer['learn_menu']>()
    const [learnPageObject, setLearnPageObject] = useState<PageDetailSiteSerializer>()
    const [locationSearch, setLocationSearch] = useState<string>(location.search)
    const [menuOpen, setMenuOpen] = useState(false)
    const [pageContentItems, setPageContentItems] = useState<PortfolioPageContentListSiteSerializer[] | undefined>(undefined)
    const [pageObject, setPageObject] = useState<PageDetailSiteSerializer>()

    const [fontFamily, setFontFamily] = useState<string>()
    const [linkColor, setLinkColor] = useState<string>()
    const [newPageStyles, setNewPageStyles] = useState<{ [key: string]: string | undefined }>({})
    const [previousLearnPageSlug, setPreviousLearnPageSlug] = useState<string | undefined>(undefined)
    const [previousReduxFormSiteRefreshLearnPage, setPreviousReduxFormSiteRefreshLearnPage] = useState<number | undefined>(undefined)

    useEffect(() => {
        if ((learnPageSlug !== previousLearnPageSlug) || (reduxFormSiteRefreshLearnPage !== previousReduxFormSiteRefreshLearnPage)) {
            getPortfolioData()
            setPreviousLearnPageSlug(learnPageSlug)
            setPreviousReduxFormSiteRefreshLearnPage(reduxFormSiteRefreshLearnPage)
        } else {
            getPortfolioData(false, true)
        }
    }, [
        detailId,
        learnPageSlug,
        pageSlug,
        reduxFormSiteRefreshLearnPage,
    ])

    useEffect(() => {
        const parsed = parseQuerySite(location.search)
        if (parsed.query || parsed.filter_query) {
            getPortfolioData(true)
        } else if (locationSearch) {
            getPortfolioData(true)
        }
        setLocationSearch(location.search)
    }, [
        location.search,
    ])

    useEffect(() => {
        if (languageId !== reduxModalSite.languageId) {
            getPortfolioData()
            setLanguageId(reduxModalSite.languageId)
        } else if (currencyId !== reduxModalSite.currencyId) {
            getPortfolioData()
            setCurrencyId(reduxModalSite.currencyId)
        }
    }, [
        reduxModalSite.currencyId,
        reduxModalSite.languageId,
    ])

    useEffect(() => {
        const tempPageStyles: { [key: string]: string | undefined } = {}
        if (getStylesToRetrieve(reduxModalSite, undefined, pageObject?.styles, '', 'backgroundImage')) {
            tempPageStyles.backgroundImage = getStylesToRetrieve(reduxModalSite, undefined, pageObject?.styles, '', 'backgroundImage')
        } else {
            tempPageStyles.background = getStylesToRetrieve(reduxModalSite, undefined, pageObject?.styles, '', 'background')
        }
        tempPageStyles.color = getStylesToRetrieve(reduxModalSite, undefined, pageObject?.styles, '', 'color')
        if (reduxModalSite.navbarType === 'top') {
            tempPageStyles.paddingTop = `${reduxModalSite.navbarIsTransparent ? 0 : reduxModalSite.navbarHeight}px`
        } else if (reduxModalSite.navbarType === 'side') {
            tempPageStyles.paddingLeft = `${reduxModalSite.navbarWidth}px`
        }
        setFontFamily(getStylesToRetrieve(reduxModalSite, undefined, reduxCacheSite.portfolio?.styles, '', 'fontFamily'))
        setNewPageStyles(tempPageStyles)
        setLinkColor(getStylesToRetrieve(reduxModalSite, undefined, pageObject?.styles, '', 'linkColor') || getStylesToRetrieve(reduxModalSite, undefined, reduxCacheSite.portfolio?.styles, '', 'linkColor'))
    }, [
        pageObject?.styles,
        reduxCacheSite.portfolio?.styles,
        reduxModalSite,
    ])

    const scrollToTop = (force?: boolean) => {
        try {
            if (history.action !== 'POP' || force) {
                // @ts-ignore
                if (document.getElementById('mo-content-detail-id-site') && document.getElementById('mo-content-detail-id-site').scrollTo) document.getElementById('mo-content-detail-id-site').scrollTo(0, 0)
            }
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'LearnPageSite',
                'scrollToTop',
            ))
        }
    }

    function getPortfolioData(noScollTop?: boolean, noLearnMenu?: boolean) {
        try {
            getDataSite({
                axiosCancelToken,
                component: 'LearnPageSite',
                detailId,
                devId,
                dispatch,
                errorStatus,
                learnPageSlug,
                location,
                noEmpty: noScollTop,
                pageSlug,
                reduxAuth,
                reduxCacheSite,
                reduxModalSite,
                scrollToTop: noScollTop ? undefined : scrollToTop,
                setAxiosCancelToken,
                setErrorStatus,
                setIsLoading,
                setLearnPageObject: (value: any) => {
                    setLearnPageObject(value)
                    if (!noLearnMenu) setLearnMenu(value?.learn_menu)
                },
                setPageContentItems,
                setPageObject,
            })
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'LearnPageSite',
                'getPortfolioData',
            ))
        }
    }

    function handleNext(item: any) {
        try {
            setIsLoadingNext(true)
            const postUrl = getApiUrlSiteV2('builder/form/learningcontent/mark-as-done/', reduxModalSite)
            if (process.env.NODE_ENV === 'development') console.log(postUrl)
            const formData: any = new FormData()
            formData.append('portfolio_id', reduxCacheSite.portfolio?.id!)
            formData.append('learn_page_id', learnPageObject?.id)
            formData.append('project_id', item.object_id)

            setLearnMenu(learnMenu?.map(obj => {
                if (obj.object_id === item.object_id) {
                    return { ...obj, is_completed: true }
                }
                return obj
            }))

            axios({
                data: formData,
                headers: getAxiosHeadersSite(reduxAuth, reduxModalSite, dispatch),
                method: 'post',
                url: postUrl,
            })
                .then((response) => {
                    setIsLoadingNext(false)
                })
                .catch((error) => {
                    setIsLoadingNext(false)
                    axiosErrorHandlerSite({
                        apiUrl: postUrl,
                        component: 'LearnPageSite',
                        dispatch,
                        error,
                        reduxAuth,
                        reduxModalSite,
                        reference: 'handleNext',
                    })
                })
        } catch (error) {
            setIsLoadingNext(false)
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'LearnPageSite',
                'handleNext',
            ))
        }
    }

    function handleResetQuizz(item: any) {
        try {
            setIsLoadingNext(true)
            const postUrl = getApiUrlSiteV2('builder/form/learningcontent/reset-quizz/', reduxModalSite)
            if (process.env.NODE_ENV === 'development') console.log(postUrl)
            const formData: any = new FormData()
            formData.append('portfolio_id', reduxCacheSite.portfolio?.id!)
            formData.append('learn_page_id', learnPageObject?.id)
            formData.append('project_id', item.object_id)

            axios({
                data: formData,
                headers: getAxiosHeadersSite(reduxAuth, reduxModalSite, dispatch),
                method: 'post',
                url: postUrl,
            })
                .then((response) => {
                    setIsLoadingNext(false)
                    dispatch(reduxFormRefreshLearnPageSite())
                })
                .catch((error) => {
                    setIsLoadingNext(false)
                    axiosErrorHandlerSite({
                        apiUrl: postUrl,
                        component: 'LearnPageSite',
                        dispatch,
                        error,
                        reduxAuth,
                        reduxModalSite,
                        reference: 'handleResetQuizz',
                    })
                })
        } catch (error) {
            setIsLoadingNext(false)
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'LearnPageSite',
                'handleResetQuizz',
            ))
        }
    }

    if (errorStatus.portfolio || errorStatus.page || errorStatus.pageCcontent) {
        return (
            <NotFoundPortfolioSite statusCode={errorStatus.portfolio || errorStatus.page || errorStatus.pageCcontent} />
        )
    }
    if (learnPageObject?.is_private && !reduxAuth?.authenticated) {
        return (
            <NotFoundPortfolioSite statusCode={403} />
        )
    }
    if (learnPageObject?.is_private && reduxAuth?.authenticated && learnPageObject.portfolio_user_groups?.length! > 0 && !checkPorfolioUserGroups({ portfolioId: reduxCacheSite.portfolio?.id!, pageUserGroups: learnPageObject.portfolio_user_groups!, reduxAuth })) {
        return (
            <NotFoundPortfolioSite statusCode={403} />
        )
    }

    const index = learnMenu?.findIndex(obj => obj.get_absolute_url?.split("/").filter(Boolean).pop() === detailId)
    const currentItem = learnMenu?.[index!]
    const nextItem = index !== -1 && index !== undefined ? learnMenu![index + 1] : undefined

    return (
        <IonPage>
            <SeoBlockSite data={pageObject?.hub_content?.seo_data || pageObject?.seo_data} />
            <SeoBlockSite data={undefined} />
            <IonContent className={`mo-hide-ion-content-scrollbar${reduxModalSite.isWindows} ${deviceType}`}>
                {(isLoadingNext) && (
                    <LoaderSite isOpen />
                )}
                {reduxCacheSite.portfolio && (
                    <div
                        className={`learn-page-site ${learnPageSlug} lg-${reduxModalSite.languageId} ${deviceType}`}
                        style={newPageStyles}
                    >
                        <div
                            className={`lps-content ${deviceType}`}
                            style={{
                                fontFamily: fontFamily,
                                height: `calc(100vh - ${reduxModalSite.navbarHeight}px)`,
                            }}
                        >
                            <div
                                className={`lps-left mo-hidden-vertical-scrollbar${reduxModalSite.isWindows}${menuOpen ? ' open' : ''} ${deviceType}`}
                                style={{
                                    height: deviceType === 'is-mobile' ? `calc(100vh - ${reduxModalSite.navbarHeight}px - 50px)` : `calc(100vh - ${reduxModalSite.navbarHeight}px)`,
                                }}
                            >
                                <p className={`lps-left-title ${learnPageSlug}`}>Le {learnPageSlug}</p>
                                {learnMenu?.map((val) => (
                                    <LearnSideMenuTabSite
                                        key={val.id}
                                        learnPageSlug={learnPageSlug}
                                        object={val}
                                    />
                                ))}
                            </div>
                            <div
                                className={`lps-right mo-hidden-vertical-scrollbar${reduxModalSite.isWindows} ${deviceType}`}
                                style={{
                                    height: `calc(100vh - ${reduxModalSite.navbarHeight}px)`,
                                }}
                            >
                                {isLoading && (
                                    <LoaderPageSite isOpen />
                                )}
                                {!isLoading && pageContentItems?.filter(obj => obj.active === true).map((val) => (
                                    <ContentBlockSite
                                        key={val.id}
                                        detailId={detailId}
                                        devId={devId}
                                        isEditHovered={false}
                                        isInDetailPage
                                        learnPageSlug={learnPageSlug}
                                        learnItem={currentItem}
                                        linkColor={linkColor}
                                        object={val}
                                        pageSlug={pageSlug}
                                        stylesEdit={undefined}
                                    />
                                ))}
                                {!isLoading && (
                                    <div className='lps-right-menu-button'>
                                        {currentItem?.has_form && currentItem.is_form_completed && (
                                            <LinkHelperSite
                                                action='generic_link'
                                                className={`lps-next-button ${learnPageSlug} ${deviceType}`}
                                                disabled={currentItem?.has_form && !currentItem?.is_form_completed}
                                                onClick={() => handleResetQuizz(currentItem)}
                                                to={undefined}
                                                style={{}}
                                            >
                                                Recommencer le quiz
                                            </LinkHelperSite>
                                        )}
                                        <LinkHelperSite
                                            action='generic_link'
                                            className={`lps-next-button ${learnPageSlug} ${deviceType}`}
                                            disabled={currentItem?.has_form && !currentItem?.is_form_completed}
                                            onClick={() => handleNext(currentItem)}
                                            to={nextItem?.get_absolute_url}
                                            style={{}}
                                        >
                                            Marquer complété et passer au suivant
                                        </LinkHelperSite>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </IonContent>
        </IonPage>
    )
})
